import { supabase } from "./supabase";
import { formatCompositeId, guidelines } from "@nrl/shared";
import { appNow } from "./appClock";


export function mergePackDraws(existing, draws) {
  const ids = [...existing.ids];
  const copies = { ...existing.copies };

  draws.forEach((draw) => {
    const id = formatCompositeId({ rarity: draw.rarity, legacyNum: draw.legacy_num });
    if (!ids.includes(id)) ids.push(id);
    copies[id] = draw.copies_after;
  });

  return { ids, copies };
}

export function packDuplicateLabel(draw) {
  if (!draw.duplicate) return null;
  return draw.copies_after >= 50
    ? "+1 copy — at the 50-copy cap"
    : `+1 copy — ${draw.copies_after} owned`;
}

export async function fetchMyCollection() {
  try {
    const { data, error } = await supabase
      .from("card_instances")
      .select("definition_id,rarity,copies,locked_copies,source_pack,acquired_at,definition:card_definitions(legacy_num)");

    if (error) throw error;
    const ids = [];
    const copies = {};
    const lockedCopies = {};
    (data || []).forEach((row) => {
      const id = formatCompositeId({ rarity: row.rarity, legacyNum: row.definition.legacy_num });
      if (!ids.includes(id)) ids.push(id);
      copies[id] = row.copies;
      lockedCopies[id] = row.locked_copies || 0;
    });
    return { ids, copies, lockedCopies, count: ids.length };
  } catch (error) {
    console.warn("Unable to fetch collection", error);
    return { ids: null, copies: null, lockedCopies: null, count: 0 };
  }
}

export async function fetchMyGrants(userId) {
  if (!userId) return [];
  try {
    const { data, error } = await supabase
      .from("pack_grants")
      .select("id,kind,cards,created_at")
      .eq("user_id", userId)
      .eq("status", "granted")
      .order("created_at", { ascending: false });

    if (error) throw error;
    return (data || []).map((row) => ({
      id: row.id,
      kind: row.kind,
      cards: row.cards,
      createdAt: row.created_at,
    }));
  } catch (error) {
    console.warn("Unable to fetch pack grants", error);
    return [];
  }
}

// The standard-pack countdown cadence is driven by the newest standard grant's
// created_at — the SAME field the server's claim_standard_pack RPC keys on
// (it selects max(created_at) where kind='standard', status-agnostic). Opening a
// granted pack flips its row to status='opened', but the cadence window is still
// measured from that row's created_at. fetchMyGrants (above) keeps its
// status='granted' filter because it feeds the unopened-packs card list; the
// countdown must use this status-agnostic query so an opened standard grant does
// not vanish from its data source after reload (otherwise nextStandardPackAt
// returned null and both tabs falsely offered Claim until a 422 not_eligible).
export async function fetchNewestStandardGrant(userId) {
  if (!userId) return null;
  try {
    const { data, error } = await supabase
      .from("pack_grants")
      .select("id,kind,cards,created_at")
      .eq("user_id", userId)
      .eq("kind", "standard")
      .order("created_at", { ascending: false })
      .limit(1);

    if (error) throw error;
    const row = (data || [])[0];
    if (!row) return null;
    return {
      id: row.id,
      kind: row.kind,
      cards: row.cards,
      createdAt: row.created_at,
    };
  } catch (error) {
    console.warn("Unable to fetch newest standard grant", error);
    return null;
  }
}

async function importError(error) {
  if (error?.context && typeof error.context.json === "function") {
    try {
      const body = await error.context.json();
      const code = ["already_imported", "invalid_input", "unauthenticated", "server_error"].includes(body?.error)
        ? body.error
        : "server_error";
      return { ok: false, code };
    } catch {
      return { ok: false, code: "server_error" };
    }
  }
  return { ok: false, code: "network_error" };
}

export async function importBridge(compositeIds) {
  try {
    const { data, error } = await supabase.functions.invoke("import-collection", {
      body: { cards: compositeIds },
    });
    if (error) return importError(error);
    return {
      ok: true,
      imported: data.imported,
      skippedUnknown: data.skipped_unknown,
    };
  } catch {
    return { ok: false, code: "network_error" };
  }
}

export function nextStandardPackAt(grants, now = appNow()) {
  const standard = (grants || []).filter((g) => g && g.kind === "standard");
  if (standard.length === 0) return null;
  const everyMs = guidelines.packs.standard.everyHours * 60 * 60 * 1000;
  let newest = -Infinity;
  for (const g of standard) {
    const t = new Date(g.createdAt).getTime();
    if (Number.isNaN(t)) continue;
    if (t > newest) newest = t;
  }
  if (!Number.isFinite(newest)) return null;
  const nextAt = newest + everyMs;
  if (nextAt <= now.getTime()) return null;
  return new Date(nextAt);
}
